import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SEO } from '../components/SEO';
import { getGeoLocation, submitLead, countryCodes, findCountryByCode } from '../services/api';

export const Contact = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    selectedCountry: countryCodes[0],
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  useEffect(() => {
    getGeoLocation().then(geo => {
      if (geo?.data) {
        setGeoData(geo);
        const matched = findCountryByCode(geo.data.countryCode);
        setFormData(prev => ({ ...prev, selectedCountry: matched }));
      }
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.country-dropdown-container')) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, '') }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({ ...prev, selectedCountry: country }));
    setIsCountryDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) { setError('Please enter your name'); return; }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address'); return;
    }
    if (!formData.phone || formData.phone.length < 7) { setError('Please enter a valid phone number'); return; }
    if (!formData.message.trim()) { setError('Please enter a message'); return; }

    setIsSubmitting(true);
    try {
      const result = await submitLead({
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: formData.selectedCountry.dialCode,
        country: formData.selectedCountry.country,
        phoneNumber: formData.phone,
        description: formData.message.trim(),
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        ipAddress: geoData?.data?.ipAddress || '',
        geoLocation: geoData ? JSON.stringify(geoData) : ''
      });

      if (result.success) {
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      content: 'One Sansome Street, Sansome St Ste 1400, San Francisco, CA 94104',
      link: 'https://maps.google.com/?q=One+Sansome+Street+San+Francisco+CA+94104'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: '+1 (888) 750-5204',
      link: 'tel:+18887505204'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@legalemphasis.com',
      link: 'mailto:info@legalemphasis.com'
    },
    {
      icon: Clock,
      title: 'Office Hours',
      content: 'Monday - Friday: 9:00 AM - 6:00 PM',
      link: null
    }
  ];

  return (
    <div className="page-wrapper" data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Contact Legal Emphasis for expert legal assistance. Located in San Francisco, CA. Call +1 (888) 750-5204 or email info@legalemphasis.com. We respond within 24 hours."
        keywords="contact lawyer, legal consultation, San Francisco attorney, law firm contact, legal help"
      />
      
      {/* Hero Section */}
      <section className="bg-[#0A2A5E] py-20 md:py-32" data-testid="contact-hero">
        <div className="le-container">
          <div className="max-w-3xl">
            <h1 className="le-heading-1 text-white mb-6">Contact Us</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Have a legal question or need assistance? Get in touch with our team and we'll respond promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="le-section bg-white" data-testid="contact-content">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="le-heading-2 mb-8">Get In Touch</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Whether you're facing a complex legal challenge or simply have questions about how we can help your business, we're here to assist. Reach out to us through any of the following methods.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div 
                      key={index}
                      className="flex items-start gap-4"
                      data-testid={`contact-info-${index}`}
                    >
                      <div className="w-12 h-12 bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#0A2A5E]" size={22} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0A2A5E] mb-1">{item.title}</h4>
                        {item.link ? (
                          <a 
                            href={item.link}
                            className="text-gray-600 hover:text-[#0A2A5E] transition-colors"
                            target={item.link.startsWith('http') ? '_blank' : undefined}
                            rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-gray-600">{item.content}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Additional Info Box */}
              <div className="mt-10 p-6 bg-[#F8F9FA] border border-[#E2E8F0]">
                <h4 className="font-semibold text-[#0A2A5E] mb-3">Need Immediate Assistance?</h4>
                <p className="text-gray-600 text-sm mb-4">
                  Our team is available during business hours for urgent legal matters. Don't hesitate to call us directly.
                </p>
                <a 
                  href="tel:+18887505204"
                  className="le-btn-primary inline-flex items-center gap-2"
                  data-testid="contact-call-direct"
                >
                  <Phone size={18} />
                  Call Now
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#F8F9FA] p-8 md:p-10" data-testid="contact-form-container">
                <h3 className="text-2xl font-semibold text-[#0A2A5E] mb-6">Send Us a Message</h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 text-sm" data-testid="contact-error">
                      {error}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-1 le-input bg-white"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1 le-input bg-white"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Country Display */}
                  {geoData?.data?.countryName && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Country</Label>
                      <div className="mt-1 px-4 py-3 text-sm bg-gray-100 border border-gray-200 text-gray-600" data-testid="contact-country-display">
                        {geoData.data.countryName}
                      </div>
                    </div>
                  )}

                  {/* Phone */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <div className="relative country-dropdown-container">
                        <button
                          type="button"
                          onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                          className="flex items-center gap-1.5 px-3 py-3 bg-white border border-gray-200 text-gray-700 
                                     hover:bg-gray-50 focus:outline-none focus:border-[#0A2A5E] transition-all text-sm min-w-[80px]"
                          data-testid="contact-country-select"
                        >
                          <span className="text-xs font-medium">+{formData.selectedCountry.dialCode}</span>
                          <svg className={`w-3 h-3 text-gray-400 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isCountryDropdownOpen && (
                          <div className="absolute bottom-full left-0 mb-1 w-44 bg-white border border-gray-200 shadow-lg z-50 max-h-48 overflow-y-auto">
                            {countryCodes.map((c, index) => (
                              <button
                                key={`${c.country}-${index}`}
                                type="button"
                                onClick={() => handleCountrySelect(c)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <span>{c.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone number"
                        inputMode="numeric"
                        className="flex-1 le-input bg-white"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please describe your legal matter..."
                      className="mt-1 le-input bg-white min-h-[120px] resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="le-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    By submitting, you agree to our <a href="/privacy" className="text-[#0A2A5E] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Response Section */}
      <section className="py-16 bg-[#0A2A5E]" data-testid="contact-quick-response">
        <div className="le-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Need Immediate Assistance?</h3>
              <p className="text-gray-300">Our team is available during business hours for urgent matters.</p>
            </div>
            <a 
              href="tel:+18887505204"
              className="bg-white text-[#0A2A5E] px-8 py-4 font-semibold uppercase tracking-wider 
                         hover:bg-gray-100 transition-all duration-300 flex items-center gap-2"
              data-testid="contact-call-btn"
            >
              <Phone size={18} />
              Call +1 (888) 750-5204
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
